<template>
  <div class="subscription-manager">
    <!-- Menu Trigger -->
    <v-menu offset-y content-class="modern-menu">
      <template v-slot:activator="{ on, attrs }">
        <div class="account-control">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="control-btn"
          >
            <v-icon v-if="!currentUser.loggedIn" color="secondary">
              mdi-incognito
            </v-icon>
            <v-icon v-else color="secondary">
              mdi-account-circle-outline
            </v-icon>
          </v-btn>
          <div class="status-badges" v-if="user.is_pro || user.is_pro_team">
            <ProFeatureChip
              v-if="user.is_pro"
              :is-pro="user.is_pro"
              :is-team="false"
              :is-active="user.is_pro"
              :is-team-pro="false"
              :show-icon="true"
              :has-shimmer="true"
              :has-pulse="isRecentUpgrade"
              :custom-tooltip="getProTooltip"
            />
            <ProFeatureChip
              v-if="user.is_pro_team"
              :is-pro="false"
              :is-team="true"
              :is-active="user.is_pro_team"
              :is-team-pro="true"
              :show-icon="true"
              :has-shimmer="true"
              :has-pulse="isRecentUpgrade"
              :custom-tooltip="getTeamTooltip"
            />
          </div>
        </div>
      </template>

      <!-- Menu Content -->
      <v-list>
        <UserInfoSection
          :user="user"
          :current-user="currentUser"
          :current-galaxy-name="currentGalaxyName"
        />

        <v-divider></v-divider>

        <SubscriptionActions
          :user="user"
          :current-user="currentUser"
          :loading="subscriptionLoading"
          @upgrade="showUpgradeConfirmation"
          @cancel="showCancelConfirmation"
          @reactivate="showReactivationConfirmation"
          @login="handleLogin"
          @logout="handleLogout"
          @navigate-to-members="navigateToMembers"
        />
      </v-list>
    </v-menu>

    <!-- Dialogs -->
    <UpgradeDialog
      v-model="showUpgradeDialog"
      :selected-plan.sync="selectedPlan"
      :loading="subscriptionLoading"
      @confirm="confirmUpgrade"
    />

    <ReactivationDialog
      v-model="showReactivationDialog"
      :reactivation-plan="reactivationPlan"
      :subscription-end-date="user.subscription_end_date"
      :loading="subscriptionLoading"
      @update:plan="reactivationPlan = $event"
      @confirm="confirmReactivation"
    />

    <CancellationDialog
      v-model="showCancelDialog"
      :user="user"
      :loading="cancelLoading"
      @confirm="confirmCancelSubscription"
    />

    <!-- Pro to Team Pro Upgrade Dialog -->
    <ProToTeamDialog
      v-model="showProToTeamDialog"
      :user="user"
      :loading="subscriptionLoading"
      @confirm="confirmProToTeamUpgrade"
    />
  </div>
</template>

<script>
import UserInfoSection from './subscription/UserInfoSection.vue';
import SubscriptionActions from './subscription/SubscriptionActions.vue';
import UpgradeDialog from './subscription/UpgradeDialog.vue';
import ReactivationDialog from './subscription/ReactivationDialog.vue';
import CancellationDialog from './subscription/CancellationDialog.vue';
import ProToTeamDialog from './subscription/ProToTeamDialog.vue';
import ProFeatureChip from './ProFeatureChip.vue';

export default {
  name: 'SubscriptionManager',

  components: {
    UserInfoSection,
    SubscriptionActions,
    UpgradeDialog,
    ReactivationDialog,
    CancellationDialog,
    ProToTeamDialog,
    ProFeatureChip,
  },

  props: {
    isPro: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
    currentUser: {
      type: Object,
      required: true,
    },
    currentGalaxyName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      showUpgradeDialog: false,
      showReactivationDialog: false,
      showCancelDialog: false,
      showProToTeamDialog: false,
      selectedPlan: null,
      reactivationPlan: 'pro',
      subscriptionLoading: false,
      cancelLoading: false,
      isRecentUpgrade: false,
    };
  },

  computed: {
    getProTooltip() {
      if (this.isRecentUpgrade) {
        return "🎉 Welcome to Pro! Enjoy unlimited access and priority support";
      }
      return "Pro subscription • Unlimited features • Priority support";
    },

    getTeamTooltip() {
      if (this.isRecentUpgrade) {
        return "🚀 Team Pro activated! Your team now has full access";
      }
      if (this.user.team_size) {
        return `Team Pro • ${this.user.team_size} members • Collaboration tools • Admin controls`;
      }
      return "Team Pro • All features for your team • Enhanced collaboration";
    }
  },

  mounted() {
    this.checkRecentUpgrade();
  },

  methods: {
    checkRecentUpgrade() {
      // Check if subscription_end_date is far in the future (recent upgrade)
      if (this.user.subscription_end_date && this.user.is_active) {
        const endDate = new Date(this.user.subscription_end_date);
        const now = new Date();
        const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

        // If subscription ends more than 350 days from now, it's likely a recent upgrade
        this.isRecentUpgrade = daysUntilEnd > 350;

        // Stop pulsing after 1 minute
        if (this.isRecentUpgrade) {
          setTimeout(() => {
            this.isRecentUpgrade = false;
          }, 60000);
        }
      }
    },

    daysSince(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },

    showUpgradeConfirmation(plan) {
      // If user is Pro and wants to upgrade to Team Pro, show special dialog directly
      if (this.user.is_pro && this.user.is_active && plan === 'team_pro') {
        this.showProToTeamDialog = true;
        return;
      }

      // If user is already active but not on any plan, show dialog directly
      if (this.user.is_active && !this.user.is_pro && !this.user.is_pro_team && plan === 'pro') {
        // Skip full dialog, directly confirm the upgrade
        this.confirmUpgrade('pro');
        return;
      }

      // For all other cases (free users, etc.), show the full upgrade dialog
      this.selectedPlan = plan;
      this.showUpgradeDialog = true;
    },

    confirmProToTeamUpgrade() {
      this.showProToTeamDialog = false;
      this.upgradeSubscription('team_pro');
    },

    confirmUpgrade(plan) {
      this.showUpgradeDialog = false;
      // Use the plan passed from the dialog
      if (plan) {
        this.upgradeSubscription(plan);
      }
    },

    showReactivationConfirmation() {
      // Set default plan based on previous subscription
      if (this.user.last_active_subscription === 'team_pro') {
        this.reactivationPlan = 'team_pro';
      } else {
        this.reactivationPlan = 'pro';
      }
      this.showReactivationDialog = true;
    },

    confirmReactivation() {
      this.showReactivationDialog = false;
      this.upgradeSubscription(this.reactivationPlan);
    },

    showCancelConfirmation() {
      this.showCancelDialog = true;
    },

    async confirmCancelSubscription() {
      this.cancelLoading = true;
      try {
        await this.$store.dispatch('websocket/cancelSubscription');
        this.showCancelDialog = false;
        this.$emit('subscription-updated');
      } catch (error) {
        console.error('Error canceling subscription:', error);
        this.$store.dispatch('websocket/showTempMessageFn', {
          message: 'Failed to cancel subscription. Please try again.',
          type: 'error',
        });
      } finally {
        this.cancelLoading = false;
      }
    },

    async upgradeSubscription(planType) {
      if (!planType || planType === 'free') {
        return; // Don't process if no plan or free plan selected
      }

      this.subscriptionLoading = true;
      try {
        await this.$store.dispatch('websocket/upgradePlan', planType);
        this.$emit('subscription-updated');
        // Trigger animation for new upgrade
        this.isRecentUpgrade = true;
        setTimeout(() => {
          this.isRecentUpgrade = false;
        }, 60000);
      } catch (error) {
        console.error(`Error upgrading to ${planType}:`, error);
        this.$store.dispatch('websocket/showTempMessageFn', {
          message: `Failed to upgrade to ${planType}. Please try again.`,
          type: 'error',
        });
      } finally {
        this.subscriptionLoading = false;
      }
    },

    handleLogin(method) {
      this.$emit('login', method);
    },

    handleLogout() {
      this.$emit('logout');
    },

    navigateToMembers() {
      this.$emit('navigate-to-members');
    },
  },
};
</script>

<style scoped>
.subscription-manager {
  position: relative;
}

.account-control {
  display: flex;
  align-items: center;
}

.status-badges {
  margin-left: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: fadeIn 0.5s ease-in-out;
}

/* Smooth entrance animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Add hover effect to the entire control area */
.account-control:hover .status-badges {
  transform: translateX(2px);
}
</style>
